import { useCallback, useEffect, useRef, useState } from 'react';

import { nadiktovat } from './api';

/**
 * ⛔ ДИКТОВКА ОТВЕТА — БРАУЗЕР ПИШЕТ ЗВУК, РАСШИФРОВЫВАЕТ НАШ СЕРВЕР
 * (`s616` Ф4, решение владельца; заменяет браузерное распознавание `s604`).
 *
 * ⛔⛔ ПОЧЕМУ ПЕРЕДЕЛАНО. Прежняя редакция звала `SpeechRecognition` — движок
 * САМОГО браузера. В Chrome он есть, в Firefox его нет вовсе, и дежурный видел
 * скрин владельца: «Этот браузер не умеет надиктовывать». Починить это со
 * стороны браузера нельзя — чинить нечего. Зато расшифровывать умеет наш
 * сервер: тот же `gigaam-asr` на своей машине, который уже расшифровывает
 * голосовые клиенток. Один распознаватель на систему, а не два.
 *
 * ⛔ НАРУЖУ НЕ УХОДИТ НИЧЕГО, И ПЛАТНОГО API ЗДЕСЬ НЕТ. Звук уезжает на наш же
 * сервер и расшифровывается локальным движком; файл живёт один запрос.
 *
 * ⛔ КЛИЕНТКЕ УХОДИТ ТЕКСТ. Мы возвращаем СЛОВА в поле ввода — отправки
 * голосовых наружу не строим (слово владельца). Речь распознаётся с ошибками
 * всегда, и правит их человек: отправка без правки означала бы «клиника
 * написала клиентке чепуху», а виноватым выглядел бы дежурный.
 *
 * ⛔ ЗАПИСЬ УМЕЮТ ВСЕ, И ПОЭТОМУ КНОПКА БОЛЬШЕ НЕ ПРЯЧЕТСЯ. `MediaRecorder`
 * есть и в Chrome, и в Firefox, и в Safari; единственный отказ, который
 * остался, — человек не дал микрофон, и это его решение, а не свойство
 * браузера. «Этот браузер не умеет» больше не показывается никому.
 *
 * ⛔ ТИП ЗАПИСИ ВЫБИРАЕТ БРАУЗЕР, А НЕ МЫ. Chrome пишет `webm/opus`, Firefox —
 * `ogg/opus`, Safari — `mp4`. Навяжи один — и снова выключишь один из
 * браузеров, то есть повторишь ровно ту беду, ради которой всё и делается.
 */

/** Потолок записи: дальше это не диктовка, а монолог. Согласован с сервером. */
const PREDEL_SEKUND = 120;

/** Умеет ли этот браузер записывать звук. Сегодня — все, но спросить надо. */
export function braouzerUmeetSlushat(): boolean {
  if (typeof window === 'undefined') return false;

  return (
    typeof window.MediaRecorder !== 'undefined' &&
    navigator.mediaDevices?.getUserMedia !== undefined
  );
}

/** Что браузер согласен писать — первый из списка, который он признаёт. */
function vybratTip(): string | undefined {
  const vidy = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
  ];

  return vidy.find((v) => window.MediaRecorder?.isTypeSupported?.(v));
}


/**
 * ⛔⛔ ЗАПИСЬ СВОДИТСЯ В WAV ПРЯМО В БРАУЗЕРЕ, И ЭТО НЕ КАПРИЗ.
 *
 * Поймал владелец на своём телефоне: «Не расслышали. GigaAM вернул HTTP 500»
 * на ЖИВОМ голосе — то есть диктовка не работала ни в одном браузере.
 *
 * Корень: `MediaRecorder` пишет ПОТОКОВЫЙ контейнер — без длительности и без
 * индекса, потому что в момент записи они ещё неизвестны. Распознаватель на
 * таком отвечает пятисоткой. Разница видна одной командой:
 *   ffprobe (обычный файл)     → длительность 6.501000
 *   ffprobe (что шлёт браузер) → длительность N/A
 *
 * ⛔ ПОЧЕМУ НЕ ПРИВЕСТИ ЗВУК НА СЕРВЕРЕ. Там это делает `ffmpeg`, и он лежит в
 * домашнем каталоге, закрытом для веб-процесса (`/home/builder`, права 750).
 * Очередь ходит туда свободно — она работает от другого пользователя, — а
 * веб-запрос нет. Открывать чужой домашний каталог наружу ради одной
 * перекодировки нельзя, а класть 80 МБ бинарника в хранилище приложения — не
 * починка, а обход.
 *
 * ⛔ ПОЭТОМУ СВОДИМ ТАМ, ГДЕ ЗВУК И РОДИЛСЯ. Браузер умеет декодировать
 * собственную запись (`decodeAudioData`) и отдать сырые отсчёты; из них
 * собирается WAV 16 кГц моно — канонический вход распознавания речи, заголовок
 * у него полный по построению. Ни `ffmpeg`, ни прав, ни зависимости от того,
 * чем именно писал браузер.
 */
const CHASTOTA_ASR = 16000;

/** Свести запись к WAV 16 кГц моно. Не вышло — отдаём как было. */
async function svestiVWav(zvuk: Blob): Promise<Blob> {
  try {
    const bayty = await zvuk.arrayBuffer();
    const kontekst = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const dekodirovano = await kontekst.decodeAudioData(bayty.slice(0));
    void kontekst.close();

    // Моно: складываем дорожки, иначе левый канал молчит на части телефонов.
    const dlina = dekodirovano.length;
    const kanalov = dekodirovano.numberOfChannels;
    const mono = new Float32Array(dlina);

    for (let k = 0; k < kanalov; k++) {
      const dorozhka = dekodirovano.getChannelData(k);
      for (let i = 0; i < dlina; i++) mono[i] += dorozhka[i] / kanalov;
    }

    // Простое прореживание до 16 кГц: для речи этого довольно, а лишние
    // библиотеки в сборку панели не тащим (стек выбран сознательно).
    const shag = dekodirovano.sampleRate / CHASTOTA_ASR;
    const skolko = Math.floor(dlina / shag);
    const otschyoty = new Int16Array(skolko);

    for (let i = 0; i < skolko; i++) {
      const v = mono[Math.floor(i * shag)] ?? 0;
      otschyoty[i] = Math.max(-1, Math.min(1, v)) * 0x7fff;
    }

    // Заголовок WAV — 44 байта, и все величины в нём известны заранее.
    const telo = new ArrayBuffer(44 + otschyoty.length * 2);
    const v = new DataView(telo);
    const slovo = (smeshchenie: number, tekst: string) => {
      for (let i = 0; i < tekst.length; i++) v.setUint8(smeshchenie + i, tekst.charCodeAt(i));
    };

    slovo(0, 'RIFF');
    v.setUint32(4, 36 + otschyoty.length * 2, true);
    slovo(8, 'WAVE');
    slovo(12, 'fmt ');
    v.setUint32(16, 16, true);
    v.setUint16(20, 1, true);            // PCM
    v.setUint16(22, 1, true);            // моно
    v.setUint32(24, CHASTOTA_ASR, true);
    v.setUint32(28, CHASTOTA_ASR * 2, true);
    v.setUint16(32, 2, true);
    v.setUint16(34, 16, true);
    slovo(36, 'data');
    v.setUint32(40, otschyoty.length * 2, true);

    for (let i = 0; i < otschyoty.length; i++) v.setInt16(44 + i * 2, otschyoty[i], true);

    return new Blob([telo], { type: 'audio/wav' });
  } catch {
    // ⛔ Не свелось — шлём как есть: на сервере стоит вторая попытка привести
    // звук, и она сработает везде, где веб-процессу доступен ffmpeg.
    return zvuk;
  }
}

export function useDiktovka(
  nashlos: (tekst: string) => void,
  neVyshlo?: (pochemu: string) => void,
) {
  const [slushaem, setSlushaem] = useState(false);
  const [rasshifrovyvaem, setRasshifrovyvaem] = useState(false);
  const pribor = useRef<MediaRecorder | null>(null);
  const potok = useRef<MediaStream | null>(null);
  const chasy = useRef<ReturnType<typeof setTimeout> | null>(null);
  /*
   * ⛔ ПРИЗНАК «ЭКРАН ЖИВ». Расшифровка идёт секунды, и за эти секунды дежурный
   * успевает уйти на другое обращение. Положить текст в поле мёртвого экрана —
   * это предупреждение React в лучшем случае и чужой черновик в худшем.
   */
  const zhivy = useRef(true);

  const prybrat = useCallback(() => {
    if (chasy.current !== null) {
      clearTimeout(chasy.current);
      chasy.current = null;
    }

    /*
     * ⛔⛔ ДОРОЖКИ МИКРОФОНА ГЛУШАТСЯ ЯВНО. Остановить писателя мало: поток
     * остаётся открытым, и браузер продолжает показывать красную точку «вас
     * слушают». Владелец увидел бы, что панель слушает его без дела — и был бы
     * прав.
     */
    potok.current?.getTracks().forEach((d) => d.stop());
    potok.current = null;
    pribor.current = null;
  }, []);

  // Уходим с экрана во время диктовки — микрофон обязан выключиться.
  useEffect(() => {
    zhivy.current = true;

    return () => {
      zhivy.current = false;

      try {
        pribor.current?.stop();
      } catch {
        // Писателя уже нет — глушить нечего.
      }

      prybrat();
    };
  }, [prybrat]);

  const ostanovit = useCallback(() => {
    try {
      pribor.current?.stop();
    } catch {
      prybrat();
      setSlushaem(false);
    }
  }, [prybrat]);

  const nachat = useCallback(async () => {
    let dorozhka: MediaStream;

    try {
      dorozhka = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      /*
       * ⛔ ОТКАЗ МИКРОФОНА — ЭТО РЕШЕНИЕ ЧЕЛОВЕКА, А НЕ ПОЛОМКА. Кнопка гаснет
       * и говорит словами, почему ничего не вышло; «нажатой навсегда» она не
       * остаётся никогда.
       */
      neVyshlo?.('mikrofon_ne_dali');
      setSlushaem(false);

      return;
    }

    const tip = vybratTip();
    let pisatel: MediaRecorder;

    try {
      pisatel = new MediaRecorder(dorozhka, tip === undefined ? undefined : { mimeType: tip });
    } catch {
      dorozhka.getTracks().forEach((d) => d.stop());
      neVyshlo?.('zapis_ne_vyshla');
      setSlushaem(false);

      return;
    }

    const kuski: Blob[] = [];

    pisatel.ondataavailable = (e) => {
      if (e.data.size > 0) kuski.push(e.data);
    };

    pisatel.onerror = () => {
      prybrat();
      setSlushaem(false);
      neVyshlo?.('zapis_ne_vyshla');
    };

    pisatel.onstop = () => {
      const vid = pisatel.mimeType || tip || 'audio/webm';
      const zvuk = new Blob(kuski, { type: vid });

      prybrat();
      setSlushaem(false);

      // Пустая запись — человек нажал и сразу отпустил. Сервер дёргать не за чем.
      if (zvuk.size === 0) return;

      setRasshifrovyvaem(true);

      void svestiVWav(zvuk)
        .then((gotovoe) => nadiktovat(gotovoe))
        .then((tekst) => {
          if (zhivy.current) nashlos(tekst);
        })
        .catch((e: unknown) => {
          if (zhivy.current) {
            neVyshlo?.(e instanceof Error ? e.message : 'rasshifrovka_ne_vyshla');
          }
        })
        .finally(() => {
          if (zhivy.current) setRasshifrovyvaem(false);
        });
    };

    potok.current = dorozhka;
    pribor.current = pisatel;

    pisatel.start();
    setSlushaem(true);

    // ⛔ Забыли остановить — остановим сами: микрофон не имеет права гореть вечно.
    chasy.current = setTimeout(() => ostanovit(), PREDEL_SEKUND * 1000);
  }, [nashlos, neVyshlo, ostanovit, prybrat]);

  const pereklyuchit = useCallback(() => {
    // Пока расшифровываем — нажатие не заводит вторую запись поверх первой.
    if (rasshifrovyvaem) return;

    if (slushaem) {
      ostanovit();

      return;
    }

    void nachat();
  }, [nachat, ostanovit, rasshifrovyvaem, slushaem]);

  return { slushaem, rasshifrovyvaem, pereklyuchit };
}
